import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getCargosControllerListarQueryKey,
  useCargosControllerAtualizar,
  useCargosControllerCriar,
} from '@/api/generated/admin-cargos/admin-cargos';
import type { CargoResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

const schema = z.object({
  nome: z.string().trim().min(3, 'Informe um nome com ao menos 3 caracteres').max(60),
  descricao: z.string().trim().max(300).optional(),
});

type Formulario = z.infer<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  cargo?: CargoResponse;
}

export function CargoFormDialogo({ aberto, aoFechar, cargo }: Props) {
  const queryClient = useQueryClient();
  const editando = Boolean(cargo);

  const criar = useCargosControllerCriar();
  const atualizar = useCargosControllerAtualizar();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  // O diálogo é montado uma vez e reusado: sem isto, abrir para editar o cargo
  // B depois de A mostraria os dados de A.
  useEffect(() => {
    if (aberto) reset({ nome: cargo?.nome ?? '', descricao: cargo?.descricao ?? '' });
  }, [aberto, cargo, reset]);

  const aoEnviar = handleSubmit(async (dados) => {
    const corpo = { nome: dados.nome, descricao: dados.descricao || null };

    try {
      if (cargo) {
        await atualizar.mutateAsync({ id: cargo.id, data: corpo });
      } else {
        await criar.mutateAsync({ data: { ...corpo, permissoes: [] } });
      }

      await queryClient.invalidateQueries({ queryKey: getCargosControllerListarQueryKey() });
      toast.success(editando ? 'Cargo atualizado.' : 'Cargo criado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o cargo.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar cargo' : 'Novo cargo'}</DialogTitle>
          <DialogDescription>
            {editando
              ? 'O identificador interno do cargo não muda ao renomear.'
              : 'As permissões são definidas depois de criar, na ação "Permissões".'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo id="nome" rotulo="Nome" erro={errors.nome?.message} obrigatorio>
            <Input id="nome" className={CLASSE_CONTROLE} placeholder="Ex.: Recepcionista" {...register('nome')} />
          </Campo>

          <Campo
            id="descricao"
            rotulo="Descrição"
            erro={errors.descricao?.message}
            dica="Ajuda quem for atribuir o cargo a entender o que ele permite."
          >
            <Textarea id="descricao" className={CLASSE_CONTROLE} rows={3} {...register('descricao')} />
          </Campo>

          {editando && cargo && (
            <p className="text-xs text-muted-foreground">
              Identificador interno: <code className="text-brand">{cargo.chave}</code>
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Criar cargo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
